import React, { useState } from 'react';

const STEPS = {
  EMAIL: 'EMAIL',
  CODE: 'CODE',
  NAME: 'NAME',
};

export default function AuthModal({ onClose, onSendCode, onVerify }) {
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setSending(true);
    setError('');
    try {
      await onSendCode(email);
      setStep(STEPS.CODE);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setSending(true);
    setError('');
    try {
      await onVerify(email, code, displayName || undefined, subscribed);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white border-2 border-ink p-4 sm:p-8 max-w-sm w-full relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-ink-faint hover:text-ink text-xl font-body"
        >
          &times;
        </button>

        <h2 className="text-2xl font-serif font-black text-center text-ink mb-1">
          Sign In
        </h2>
        <div className="w-16 mx-auto border-t-2 border-ink mt-1 mb-6" />

        {step === STEPS.EMAIL && (
          <form onSubmit={handleSendCode}>
            <label className="block text-sm font-body text-ink-light mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-2 border-ink px-3 py-2 font-body text-ink mb-1 focus:outline-none"
              placeholder="you@example.com"
              autoFocus
            />
            <p className="text-xs text-ink-faint font-body mb-4">
              We'll send you a sign-in code. No password needed.
            </p>

            <label className="block text-sm font-body text-ink-light mb-1">
              Display name (optional)
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border-2 border-ink px-3 py-2 font-body text-ink mb-4 focus:outline-none"
              placeholder="How you'll appear on the leaderboard"
            />

            <label className="flex items-start gap-2 mb-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={subscribed}
                onChange={(e) => setSubscribed(e.target.checked)}
                className="mt-1 w-4 h-4 accent-ink cursor-pointer"
              />
              <span className="text-sm font-body text-ink-light">
                Email me the daily puzzle (6 AM ET). You can change this anytime in your profile.
              </span>
            </label>

            {error && <p className="text-red-600 text-sm font-body mb-3">{error}</p>}

            <button
              type="submit"
              disabled={sending}
              className="w-full py-3 bg-ink text-cream font-serif font-bold border-2 border-ink hover:bg-ink-light transition-colors disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Send Code'}
            </button>
          </form>
        )}

        {step === STEPS.CODE && (
          <form onSubmit={handleVerify}>
            <p className="text-sm font-body text-ink-light mb-4 text-center">
              Check <strong>{email}</strong> for your 6-digit code.
            </p>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full border-2 border-ink px-3 py-3 font-body text-ink text-center text-2xl tracking-[0.5em] mb-4 focus:outline-none"
              placeholder="000000"
              autoFocus
              maxLength={6}
            />

            {error && <p className="text-red-600 text-sm font-body mb-3">{error}</p>}

            <button
              type="submit"
              disabled={sending}
              className="w-full py-3 bg-ink text-cream font-serif font-bold border-2 border-ink hover:bg-ink-light transition-colors disabled:opacity-50"
            >
              {sending ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => { setStep(STEPS.EMAIL); setCode(''); setError(''); }}
              className="w-full mt-2 py-2 text-sm text-ink-faint font-body hover:text-ink transition-colors"
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
